import { exec, getApiToken } from '@src/apis/reware/utils';
import type { ILookerFilters, ILookerUserAttributes } from './types';

export interface FetchLookerEmbedUrlArgs {
  partnerCode: string;
  dashboardId: string;
  token: string;
  userAttributes?: ILookerUserAttributes;
  filters?: ILookerFilters;
}

interface FetchLookerEmbedUrlExecVariables {
  token: string;
  partnerCode: string;
  dashboardId: number;
  userAttributes?: ILookerUserAttributes;
  filters?: ILookerFilters;
}

interface FetchLookerEmbedUrlExecResponse {
  restUser: {
    lookerEmbedUrl: string | null;
  };
}

/**
 * Fetches a signed Looker embed URL for a specific dashboard, resolved on the
 * backend from the partner's lookerServiceConfig (service id + client
 * credentials).
 */
export const fetchLookerEmbedUrl = async (
  { partnerCode, dashboardId, token, userAttributes, filters }: FetchLookerEmbedUrlArgs
): Promise<string | null> => {
  if (!partnerCode) {
    throw new Error('Partner code is required');
  }
  if (!dashboardId) {
    throw new Error('Dashboard ID is required');
  }

  const parsedDashboardId = parseInt(dashboardId, 10);
  if (isNaN(parsedDashboardId)) {
    throw new Error(`Invalid dashboard ID: ${dashboardId}`);
  }

  const apiToken = await getApiToken(token);

  const query = `
    query getLookerEmbedUrl(
      $token: String!
      $partnerCode: String!
      $dashboardId: Int!
      $userAttributes: JSON
      $filters: JSON
    ) {
      restUser(token: $token) {
        lookerEmbedUrl(
          partnerCode: $partnerCode
          dashboardId: $dashboardId
          userAttributes: $userAttributes
          filters: $filters
        )
      }
    }
  `;

  const data = await exec<FetchLookerEmbedUrlExecVariables, FetchLookerEmbedUrlExecResponse>(
    query,
    {
      variables: {
        token: apiToken,
        partnerCode,
        dashboardId: parsedDashboardId,
        userAttributes,
        filters,
      },
    }
  );

  return data.restUser.lookerEmbedUrl;
};
